import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useModerator } from '@/hooks/useModerator'
import { Navigate } from 'react-router-dom'
import { format, formatDistanceToNow } from 'date-fns'
import ReportDetail from '@/components/ReportDetail'
import AuditLog from '@/components/AuditLog'
import { useAdminModerationStats } from '@/hooks/useAdminModerationStats'

interface Report {
  id: string
  target_type: 'thread' | 'comment' | 'post'
  target_id: string
  reporter_id: string | null
  reason: string
  status: 'pending' | 'reviewed' | 'dismissed'
  created_at: string
  reporter: {
    display_name: string
  } | null
}

export default function Moderation() {
  const { user } = useAuth()
  const isModerator = useModerator()
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'reviewed' | 'dismissed'>('pending')
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null)
  const [showAuditLog, setShowAuditLog] = useState(false)
  const [metricsDays, setMetricsDays] = useState<30 | 90>(30)

  const metricsDateRange = useMemo(() => {
    const to = new Date()
    const from = new Date()
    from.setDate(from.getDate() - metricsDays)
    return { from, to }
  }, [metricsDays])

  const { data: moderationStats, isLoading: statsLoading } = useAdminModerationStats(
    metricsDateRange.from,
    metricsDateRange.to
  )

  // Redirect non-moderators
  if (!user || !isModerator) {
    return <Navigate to="/" replace />
  }

  // Fetch reports
  const { data: reports, isLoading, error: reportsError } = useQuery({
    queryKey: ['reports', statusFilter],
    queryFn: async () => {
      let query = supabase
        .from('reports')
        .select(`
          id,
          target_type,
          target_id,
          reporter_id,
          reason,
          status,
          created_at,
          profiles!reports_reporter_id_fkey (
            display_name
          )
        `)
        .order('created_at', { ascending: false })

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter)
      }

      const { data, error } = await query

      if (error) {
        console.error('Error fetching reports:', error)
        throw error
      }

      // Transform data: Supabase returns profiles as array, extract first element
      return (data || []).map((report: any) => ({
        ...report,
        reporter: Array.isArray(report.profiles)
          ? (report.profiles[0] || null)
          : report.profiles,
      })) as Report[]
    },
    retry: false,
  })

  const selectedReport = reports?.find((r) => r.id === selectedReportId)

  // Count reports per content (same target_type + target_id) for list display
  const reportCountByTarget =
    reports?.reduce(
      (acc, r) => {
        const key = `${r.target_type}:${r.target_id}`
        acc[key] = (acc[key] ?? 0) + 1
        return acc
      },
      {} as Record<string, number>
    ) ?? {}

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1
          className="text-3xl font-bold"
          style={{ color: 'var(--text-primary)' }}
        >
          Moderation Dashboard
        </h1>
        <button
          onClick={() => setShowAuditLog(!showAuditLog)}
          className="px-4 py-2 font-semibold rounded"
          style={{
            backgroundColor: showAuditLog
              ? 'var(--bg-interactive-hover)'
              : 'var(--bg-interactive)',
            color: 'var(--text-inverse)',
          }}
        >
          {showAuditLog ? 'Hide Audit Log' : 'Show Audit Log'}
        </button>
      </div>

      {showAuditLog ? (
        <AuditLog />
      ) : (
        <>
          {/* Moderation metrics */}
          <section
            className="mb-6 p-4 rounded border-2"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              borderColor: 'var(--bg-tertiary)',
            }}
            aria-labelledby="moderation-metrics-heading"
          >
            <h2
              id="moderation-metrics-heading"
              className="text-xl font-bold mb-4"
              style={{ color: 'var(--text-primary)' }}
            >
              Moderation metrics
            </h2>
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <label htmlFor="metrics-days" className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                Period:
              </label>
              <select
                id="metrics-days"
                value={metricsDays}
                onChange={(e) => setMetricsDays(Number(e.target.value) as 30 | 90)}
                className="px-3 py-2 border-2 rounded"
                style={{
                  borderColor: 'var(--bg-tertiary)',
                  backgroundColor: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                }}
              >
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
              </select>
              {moderationStats && (
                <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Average resolution time:{' '}
                  {moderationStats.avgResolutionHours != null ? (
                    <strong style={{ color: 'var(--text-primary)' }}>
                      {moderationStats.avgResolutionHours.toFixed(1)} hours
                    </strong>
                  ) : (
                    <span style={{ color: 'var(--text-secondary)' }}>—</span>
                  )}
                </span>
              )}
            </div>
            {statsLoading ? (
              <p style={{ color: 'var(--text-secondary)' }}>Loading metrics…</p>
            ) : moderationStats && moderationStats.dailyRows.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse" style={{ borderColor: 'var(--bg-tertiary)' }}>
                  <caption className="sr-only">
                    Reports created per day from {moderationStats.fromDate} to {moderationStats.toDate}
                  </caption>
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        className="text-left py-2 px-3 border border-solid"
                        style={{ borderColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                      >
                        Date
                      </th>
                      <th
                        scope="col"
                        className="text-right py-2 px-3 border border-solid"
                        style={{ borderColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                      >
                        Reports
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {moderationStats.dailyRows.map((row) => (
                      <tr key={row.report_date}>
                        <td
                          className="py-2 px-3 border border-solid"
                          style={{ borderColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                        >
                          {format(new Date(row.report_date + 'Z'), 'dd MMM yyyy')}
                        </td>
                        <td
                          className="py-2 px-3 border border-solid text-right"
                          style={{ borderColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                        >
                          {row.reports_created}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : moderationStats ? (
              <p style={{ color: 'var(--text-secondary)' }}>No report activity in this period.</p>
            ) : null}
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Reports List */}
          <div className="lg:col-span-1">
            <div className="mb-4">
              <label
                htmlFor="status-filter"
                className="block mb-2 font-semibold"
                style={{ color: 'var(--text-primary)' }}
              >
                Filter by Status
              </label>
              <select
                id="status-filter"
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(
                    e.target.value as 'all' | 'pending' | 'reviewed' | 'dismissed'
                  )
                }
                className="w-full px-4 py-2 border-2 rounded"
                style={{
                  borderColor: 'var(--bg-tertiary)',
                  backgroundColor: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                }}
              >
                <option value="all">All Reports</option>
                <option value="pending">Pending</option>
                <option value="reviewed">Reviewed</option>
                <option value="dismissed">Dismissed</option>
              </select>
            </div>

            {isLoading ? (
              <p style={{ color: 'var(--text-primary)' }}>Loading reports...</p>
            ) : reportsError ? (
              <div
                className="p-4 rounded border-2"
                style={{
                  backgroundColor: 'var(--color-error)',
                  color: 'var(--text-inverse)',
                }}
              >
                <p className="font-semibold mb-2">Error loading reports:</p>
                <p className="text-sm">
                  {reportsError instanceof Error
                    ? reportsError.message
                    : 'Unknown error occurred'}
                </p>
              </div>
            ) : !reports || reports.length === 0 ? (
              <p style={{ color: 'var(--text-primary)' }}>
                No {statusFilter === 'all' ? '' : statusFilter} reports found.
              </p>
            ) : (
              <div className="space-y-2">
                {reports.map((report) => {
                  const reportDate = format(
                    new Date(report.created_at),
                    'dd/MM/yyyy, HH:mm'
                  )
                  const isSelected = selectedReportId === report.id

                  return (
                    <div
                      key={report.id}
                      onClick={() => setSelectedReportId(report.id)}
                      className="p-3 rounded border-2 cursor-pointer"
                      style={{
                        backgroundColor: isSelected
                          ? 'var(--bg-tertiary)'
                          : 'var(--bg-secondary)',
                        borderColor: 'var(--bg-tertiary)',
                      }}
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <span
                          className="px-2 py-1 text-xs font-semibold rounded"
                          style={{
                            backgroundColor:
                              report.status === 'pending'
                                ? 'var(--color-error)'
                                : report.status === 'reviewed'
                                ? 'var(--bg-interactive)'
                                : 'var(--bg-tertiary)',
                            color: 'var(--text-inverse)',
                          }}
                        >
                          {report.status}
                        </span>
                        <span
                          className="px-2 py-1 text-xs font-semibold rounded"
                          style={{
                            backgroundColor: 'var(--bg-tertiary)',
                            color: 'var(--text-primary)',
                          }}
                        >
                          {report.target_type}
                        </span>
                      </div>
                      <p
                        className="text-sm font-semibold mb-1"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        {report.reason}
                      </p>
                      <p
                        className="text-xs"
                        style={{ color: 'var(--text-secondary)' }}
                      >
                        {reportCountByTarget[`${report.target_type}:${report.target_id}`] ?? 1} reporter
                        {(reportCountByTarget[`${report.target_type}:${report.target_id}`] ?? 1) !== 1
                          ? 's'
                          : ''}{' '}
                        • {reportDate}
                      </p>
                      <p
                        className="text-xs"
                        style={{ color: 'var(--text-secondary)' }}
                        title={reportDate}
                      >
                        Time since report: {formatDistanceToNow(new Date(report.created_at), { addSuffix: true })}
                      </p>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Report Detail */}
          <div className="lg:col-span-2">
            {selectedReport ? (
              <ReportDetail
                report={selectedReport}
                onActionComplete={() => {
                  setSelectedReportId(null)
                }}
              />
            ) : (
              <div
                className="p-6 rounded border-2"
                style={{
                  backgroundColor: 'var(--bg-secondary)',
                  borderColor: 'var(--bg-tertiary)',
                }}
              >
                <p style={{ color: 'var(--text-primary)' }}>
                  Select a report from the list to view details and take action.
                </p>
              </div>
            )}
          </div>
        </div>
        </>
      )}
    </div>
  )
}

